import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';

/**
 * Mirrors `Tunet.Contracts.Dtos.Vec3` and `Quat`'s `ToArray`/`FromArray` pair — see
 * `src/shared-contracts/Tunet.Contracts/README.md`, "Why streaming messages use float[]".
 *
 * `PoseDto` and `NodeTransformUpdate` carry position and rotation as packed `number[]`
 * instead of named fields, purely to keep wire size down on messages sent 15-20 times a
 * second per peer. The C# side keeps that array layout in exactly one place; before this
 * module existed, the client re-derived it inline at every call site instead. The failure
 * mode for getting it wrong is not a crash — these messages are fire-and-forget and never
 * persisted — it is a remote avatar or node silently rendered in the wrong place, which is
 * why the element order below is exactly `Vec3.ToArray`/`Quat.ToArray`'s, and why it is
 * asserted explicitly (not just round-tripped) in packing.test.ts.
 */

/** `Vec3.ToArray`: element order (x, y, z). */
export function vec3ToArray(v: Vector3): number[] {
  return [v.x, v.y, v.z];
}

/**
 * `Vec3.FromArray`. Requires exactly 3 components, matching the `ArgumentException` the C#
 * side throws for anything else — every producer in this client (and the server, which only
 * ever relays what `Vec3.ToArray` packed) always emits exactly 3, so this is unreachable
 * today rather than a change to any real call site's behaviour.
 */
export function vec3FromArray(source: number[]): Vector3 {
  if (source.length !== 3) {
    throw new Error(`Vec3 requires exactly 3 components, got ${source.length}.`);
  }

  return new Vector3(source[0]!, source[1]!, source[2]!);
}

/** `Quat.ToArray`: element order (x, y, z, w). */
export function quatToArray(q: Quaternion): number[] {
  return [q.x, q.y, q.z, q.w];
}

/**
 * `Quat.FromArray`. Requires exactly 4 components — see `vec3FromArray`.
 */
export function quatFromArray(source: number[]): Quaternion {
  if (source.length !== 4) {
    throw new Error(`Quat requires exactly 4 components, got ${source.length}.`);
  }

  return new Quaternion(source[0]!, source[1]!, source[2]!, source[3]!);
}
